import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiskIq } from '@tri-opra/riskiq-web';
import '@tri-opra/riskiq-web/styles.css';
import { FullPageInfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { IFormData, ProtocolReviewContext } from '@pages-clean/risk-assessment/risk-iq/interfaces/context';
import { useRiskIqAvailability } from '@pages-clean/risk-assessment/risk-iq/review/use-risk-iq-availability';
import {
    SelectedRiskIqItem,
    useRiskIqEventHandler,
} from '@pages-clean/risk-assessment/risk-iq/review/use-risk-iq-event-handler';
import { NullableStudy } from '@contexts/local-state-context';
import { UserContext } from '@contexts/user-context';
import { getIsActiveField } from '@utils/convertor';
import Settings from '@settings/settings';
import { LoggedInPageProps } from '@config/routes/interfaces';
import './index.scss';

interface IProtocolReviewProps {
    study: NullableStudy | undefined;
}

const Content = ({ study }: IProtocolReviewProps) => {
    const [formData, setFormData] = useState<IFormData>({
        study,
    });
    const { user, token } = useContext(UserContext);

    // Holds the CtQ/Risk type + OPRA Mongo id resolved from the last selection-changed event, for
    // feeding to the CtQ/Risk comments component.
    const [selectedItem, setSelectedItem] = useState<SelectedRiskIqItem | null>(null);

    const StudyHasRAM = study && getIsActiveField('RAM', study.modules);

    const protocolReviewProviderValue = useMemo(
        () => ({
            current: formData,
            setCurrent: setFormData,
            selectedItem,
        }),
        [formData, setFormData, selectedItem],
    );

    const { t: tHome } = useTranslation('home');
    const { t } = useTranslation('ram');

    const apiOrigin = Settings.api.riskIqApiOrigin() || undefined;
    const bearerToken = token ?? undefined;
    const riskIqAvailability = useRiskIqAvailability(apiOrigin, bearerToken);

    useEffect(() => {
        setFormData({
            ...formData,
            study,
        });
    }, [study?.id]);

    const handleRiskIqEvent = useRiskIqEventHandler(study?.id ?? undefined, setSelectedItem);

    if (!StudyHasRAM)
        return <FullPageInfoField message={tHome('home:pageDataErrors:cannotAccessModule')} type="Info" />;

    if (riskIqAvailability === 'checking') return <Loading />;

    // RiskIQ's backend is switched off for this environment (independently of the UI flag).
    if (riskIqAvailability === 'unavailable')
        return <FullPageInfoField message={t('ram:riskIQ:unavailable')} type="Info" />;

    return (
        <div className="protocolReview-container">
            <ProtocolReviewContext.Provider value={protocolReviewProviderValue}>
                <div style={{ height: 'calc(100% - 30px)' }}>
                    <RiskIq
                        mode="opra-embedded"
                        bearerToken={bearerToken}
                        apiOrigin={apiOrigin}
                        currentUserEmail={user?.email}
                        onAppEvent={handleRiskIqEvent}
                        studyId={study.id}
                    />
                </div>
            </ProtocolReviewContext.Provider>
        </div>
    );
};

const ProtocolReviewWrapper = ({ study }: IProtocolReviewProps) => {
    const content = <Content study={study} />;

    return <>{content}</>;
};

const ProtocolReview = ({ study }: LoggedInPageProps) => <ProtocolReviewWrapper study={study} />;

export default ProtocolReview;
